
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import PageTransition from '../../components/base/PageTransition';

const plans = [
  {
    name: 'Free',
    badge: null,
    monthlyPrice: 0,
    annualPrice: 0,
    description: 'Projects that are under construction',
    cta: 'Get started for free',
    ctaStyle: 'outline',
    features: [
      'Build web & mobile apps',
      'Unlimited pages',
      'Basic templates',
      'Community support',
      '5 GB storage',
      'Your App Wizard subdomain',
      'Basic plugins',
    ],
    expandLabel: 'Free plan features',
  },
  {
    name: 'Starter',
    badge: null,
    monthlyPrice: 29,
    annualPrice: 23,
    description: 'Projects that are ready to launch',
    cta: 'Start a free trial',
    ctaStyle: 'outline',
    features: [
      'Everything in Free, plus…',
      'Custom domain',
      'Remove Your App Wizard branding',
      '10 GB storage',
      'Basic API workflows',
      'Email support',
      '2 app collaborators',
    ],
    expandLabel: 'Everything in Free, plus…',
  },
  {
    name: 'Growth',
    badge: 'Recommended',
    monthlyPrice: 119,
    annualPrice: 95,
    description: 'Best for team collaboration',
    cta: 'Get started',
    ctaStyle: 'solid',
    features: [
      'Everything in Starter, plus…',
      'Unlimited app collaborators',
      '50 GB storage',
      'Advanced API workflows',
      'Priority support',
      'Custom email domain',
      'Version control',
      'Advanced analytics',
    ],
    expandLabel: 'Everything in Starter, plus…',
  },
  {
    name: 'Team',
    badge: null,
    monthlyPrice: 349,
    annualPrice: 279,
    description: 'Best for scaling your team and business',
    cta: 'Get started',
    ctaStyle: 'outline',
    features: [
      'Everything in Growth, plus…',
      '200 GB storage',
      'Dedicated support manager',
      'Custom integrations',
      'Advanced security controls',
      'SSO / SAML login',
      'SLA guarantee',
    ],
    expandLabel: 'Everything in Growth, plus…',
  },
  {
    name: 'Enterprise',
    badge: null,
    monthlyPrice: null,
    annualPrice: null,
    description: 'Best for maximum security and scale',
    cta: 'Talk to sales',
    ctaStyle: 'outline',
    features: [
      'Everything in Team, plus…',
      'Unlimited storage',
      'White-label solution',
      'Custom SLA',
      'Dedicated infrastructure',
      'Onboarding & training',
      'Custom contracts',
    ],
    expandLabel: 'Everything in Team, plus…',
  },
];

const workloadFaqs = [
  {
    question: 'What is workload?',
    answer:
      'Your App Wizard uses a usage-based pricing model, so users will only pay for the resources their app needs. Our mission is to make software development more accessible — instead of requiring users to monitor various resources at a traditional tech stack level with cost, we introduced a single metric: workload.',
  },
  {
    question: 'How much workload will I need?',
    answer:
      'The amount of workload an app needs depends on a variety of factors. If you\'re new to building apps, the best way to assess your workload needs is to get started on a Free plan, which comes with more than enough Workload to get started. From within your free app, you will be able to see the activities unique to your app that contribute to workload usage.',
  },
  {
    question: 'Can I cap the amount of workload my app uses?',
    answer:
      'Yes, you can disable overages at any time so that your app does not use more than the amount that comes with your plan (plus any additional workload you may have purchased via a workload tier).',
  },
  {
    question: 'What are my options if I need more workload?',
    answer:
      'If you need more workload than the amount that comes with the Starter, Growth, or Team plan, you can either purchase a workload tier for additional workload, or you can pay for overages as you go. Apps on the Free plan or Agency plan cannot subscribe to workload tiers and cannot enable overages because these plans are designed for development.',
  },
];

const billingFaqs = [
  {
    question: 'Do I need a plan for each app?',
    answer:
      'Plans are purchased at the project level. Each project can include both Mobile and Web apps, a Mobile app only, or a Web app only.',
  },
  {
    question: 'Will I get notified before I\'m charged for workload overages?',
    answer:
      'Yes, you will receive an email notification when your app has reached 75% of its available workload units. You will also receive an email notification when your app has reached 100% and overages will kick in.',
  },
  {
    question: 'What are the add-ons I can purchase separate from what comes in the plans?',
    answer:
      'Workload tiers (only available on the Starter, Growth, and Team plans), additional file storage for $5 per 100 GB each month, and plugin subscriptions.',
  },
];

const nativeFaqs = [
  {
    question: 'What\'s the difference between a native mobile app vs web app?',
    answer:
      'Native mobile apps can be published to iOS and Android app stores. They\'re optimized for mobile performance and offer access to mobile device features like cameras, push notifications, and location services. Web apps, in Your App Wizard, are browser-based and can be accessed from any device with internet access, but have limited access to device capabilities compared to native apps.',
  },
  {
    question: 'When do I need a paid mobile plan?',
    answer:
      'The free plan is perfect for building your native mobile app. A paid plan is necessary for deploying a live version of your app, which is required for testing with App Test Flight and Google Play, and for publishing to the App Store or Google Play Store.',
  },
  {
    question: 'What\'s the advantage of a Mobile + Web plan?',
    answer:
      'A Mobile + Web plan allows your app to share the same backend: database, workflows, and API connections, eliminating duplicate development work and reducing the time spent creating separate experiences for web, iOS, and Android.',
  },
];

type FaqItem = { question: string; answer: string };

function FaqGroup({ title, items }: { title: string; items: FaqItem[] }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="mb-10">
      <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">{title}</h3>
      <div className="space-y-3">
        {items.map((faq, i) => (
          <div key={i} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
            <button
              onClick={() => setOpen(open === i ? null : i)}
              className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <span className="font-semibold text-gray-900 text-sm pr-4">{faq.question}</span>
              <i className={`ri-arrow-down-s-line text-xl text-gray-500 flex-shrink-0 transition-transform ${open === i ? 'rotate-180' : ''}`}></i>
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${open === i ? 'max-h-64' : 'max-h-0'}`}>
              <p className="px-6 pb-5 text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PricingPage() {
  const [isAnnual, setIsAnnual] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);

  return (
    <PageTransition>
      <div className="min-h-screen bg-white">
        <Navbar />

        {/* Hero */}
        <section className="pt-32 pb-10 px-6 text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-teal-600 mb-3">Pricing</p>
          <h1 className="text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
            Start building for free
          </h1>
          <p className="text-lg text-gray-500 max-w-xl mx-auto mb-8">
            Your App Wizard comes with everything you need to build web, iOS, and Android apps. Build any app for free, and upgrade to Starter when you're ready to launch.
          </p>
          <p className="text-sm text-gray-500 mb-10">
            Looking to build professionally?{' '}
            <Link to="/get-started" className="text-teal-600 font-semibold hover:underline cursor-pointer">
              Explore an Agency account
            </Link>
          </p>

          {/* Platform Toggle */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {['Web & Mobile', 'Web', 'Mobile'].map((tab) => (
              <button
                key={tab}
                className="px-4 py-2 rounded-full text-sm font-medium border border-gray-200 text-gray-600 hover:border-teal-500 hover:text-teal-600 transition-all cursor-pointer whitespace-nowrap"
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-3 mb-12">
            <span className={`text-sm font-medium ${!isAnnual ? 'text-gray-900' : 'text-gray-400'}`}>Monthly</span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${isAnnual ? 'bg-teal-500' : 'bg-gray-300'}`}
            >
              <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${isAnnual ? 'translate-x-6' : ''}`}></div>
            </button>
            <span className={`text-sm font-medium ${isAnnual ? 'text-gray-900' : 'text-gray-400'}`}>Annual</span>
            {isAnnual && (
              <span className="px-3 py-1 bg-teal-100 text-teal-700 text-xs font-bold rounded-full whitespace-nowrap">
                Save up to 20% annually
              </span>
            )}
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="pb-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-5 gap-4">
              {plans.map((plan, index) => (
                <div
                  key={index}
                  className={`relative flex flex-col rounded-2xl border p-6 transition-all ${
                    plan.badge
                      ? 'border-teal-500 shadow-xl shadow-teal-100 bg-white'
                      : 'border-gray-200 bg-white hover:shadow-md'
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-teal-500 text-white text-xs font-bold rounded-full whitespace-nowrap">
                      {plan.badge}
                    </div>
                  )}

                  <div className="mb-4">
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Web &amp; Mobile</p>
                    <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                    <p className="text-xs text-gray-500 mt-1 min-h-[32px]">{plan.description}</p>
                  </div>

                  <div className="mb-5">
                    {plan.monthlyPrice === null ? (
                      <p className="text-2xl font-extrabold text-gray-900">Custom</p>
                    ) : plan.monthlyPrice === 0 ? (
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-extrabold text-gray-900">$0</span>
                        <span className="text-gray-400 text-sm">/mo</span>
                      </div>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-extrabold text-gray-900">
                          ${isAnnual ? plan.annualPrice : plan.monthlyPrice}
                        </span>
                        <span className="text-gray-400 text-sm">/mo</span>
                      </div>
                    )}
                    {isAnnual && plan.annualPrice && plan.annualPrice > 0 && (
                      <p className="text-xs text-gray-400 mt-1">Billed ${(plan.annualPrice * 12).toLocaleString()}/year</p>
                    )}
                  </div>

                  <Link
                    to="/get-started"
                    className={`block text-center py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer mb-5 ${
                      plan.ctaStyle === 'solid'
                        ? 'bg-teal-500 text-white hover:bg-teal-600 shadow-md shadow-teal-200'
                        : 'border border-gray-300 text-gray-800 hover:border-teal-500 hover:text-teal-600'
                    }`}
                  >
                    {plan.cta}
                  </Link>

                  <div className="border-t border-gray-100 pt-4">
                    <button
                      onClick={() => setExpandedPlan(expandedPlan === index ? null : index)}
                      className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-teal-600 cursor-pointer mb-3 transition-colors"
                    >
                      <i className={`ri-arrow-down-s-line transition-transform ${expandedPlan === index ? 'rotate-180' : ''}`}></i>
                      {plan.expandLabel}
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${expandedPlan === index ? 'max-h-96' : 'max-h-0'}`}>
                      <ul className="space-y-2">
                        {plan.features.map((f, fi) => (
                          <li key={fi} className="flex items-start gap-2 text-xs text-gray-600">
                            <i className="ri-check-line text-teal-500 flex-shrink-0 mt-0.5"></i>
                            {f}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Show full comparison */}
            <div className="text-center mt-8">
              <button className="text-sm font-semibold text-teal-600 hover:underline cursor-pointer flex items-center gap-1 mx-auto">
                <i className="ri-arrow-down-s-line text-lg"></i>
                Show full comparison
              </button>
            </div>
          </div>
        </section>

        {/* Workload Add-ons Banner */}
        <section className="py-14 px-6 bg-gradient-to-r from-teal-50 to-cyan-50 border-y border-teal-100">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Growing quickly? Scale with workload add-ons.</h2>
              <p className="text-gray-600 text-sm max-w-xl">
                When you start to scale your user base, you can purchase any additional workload you need to take your app and your business to the next level.
              </p>
            </div>
            <Link
              to="/get-started"
              className="px-6 py-3 bg-teal-500 text-white rounded-lg font-semibold text-sm hover:bg-teal-600 transition-all whitespace-nowrap cursor-pointer shadow-md shadow-teal-200 flex-shrink-0"
            >
              Explore workload tiers
            </Link>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-20 px-6">
          <div className="max-w-3xl mx-auto">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">FAQs</p>
            <h2 className="text-4xl font-extrabold text-gray-900 mb-12">Frequently asked questions</h2>
            <FaqGroup title="Workload" items={workloadFaqs} />
            <FaqGroup title="Billing" items={billingFaqs} />
            <FaqGroup title="Native Mobile" items={nativeFaqs} />
          </div>
        </section>

        {/* Agency CTA */}
        <section className="py-16 px-6 bg-gray-900">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-10">
            <div className="flex-shrink-0 w-32 h-32 rounded-2xl bg-yellow-400 flex items-center justify-center">
              <i className="ri-building-2-line text-5xl text-gray-900"></i>
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Agencies &amp; Developers</p>
              <h2 className="text-3xl font-extrabold text-white mb-3">Building for clients?</h2>
              <p className="text-gray-400 text-sm mb-6 max-w-lg">
                Create unlimited premium apps, unlock advanced app development and collaboration features, and grow your team with business development support and a lead generation platform from Your App Wizard.
              </p>
              <Link
                to="/get-started"
                className="inline-block px-6 py-3 bg-teal-500 text-white rounded-lg font-semibold text-sm hover:bg-teal-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Get an Agency account
              </Link>
            </div>
          </div>
        </section>

        {/* Students & Nonprofits */}
        <section className="py-16 px-6 bg-white border-t border-gray-100">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10">
            <div className="flex gap-5 items-start">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-teal-100 flex-shrink-0">
                <i className="ri-graduation-cap-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Students &amp; Universities</p>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Discount to support education</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Native mobile apps can be published to iOS and Android app stores. Eligible students and educational institutions can access group discounts to support learning in the classroom.
                </p>
                <Link to="/get-started" className="text-sm font-semibold text-teal-600 hover:underline cursor-pointer">
                  Learn about student discounts →
                </Link>
              </div>
            </div>
            <div className="flex gap-5 items-start">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-teal-100 flex-shrink-0">
                <i className="ri-heart-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Non-Profits</p>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Discount to support doing good</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Your App Wizard is proud to support organizations building products and using technology to drive positive change. Non-profits can apply for special discounted pricing.
                </p>
                <Link to="/get-started" className="text-sm font-semibold text-teal-600 hover:underline cursor-pointer">
                  Learn about non-profit discounts →
                </Link>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </PageTransition>
  );
}
